import * as React from "react";
import {defineMessages, FormattedMessage, injectIntl} from "react-intl";
import {Link} from "react-router-dom";
import {ColumnsDescription, ColumnWidth} from "../common/ui/table/ControlRoomColumns";
import {createBasicTableComponent} from "../common/ui/table/ControlRoomTable";
import {connect} from "react-redux";
import {PagedTableConfig} from "../common/ui/table/PagedTable";
import {DateUtil} from "../common/util/DateUtil";
import {actions} from "./actions";
import {Style} from "./model";
import {selectors} from "./selectors";
import {selectors as pagedDataSelectors} from "../common/util/pagination/PaginationUtil";
import {sortItems, SortSettings} from "../common/ui/table/model";
import {COLUMN_SORT_SETTINGS_STYLES, localRepository} from "../common/LocalRepository";

const config: PagedTableConfig = {
    canMultiSelect: true,
    noItemsFoundMessage: <FormattedMessage id="studio.styles-list.none-found" defaultMessage="No styles found"/>,
};

const MESSAGES = defineMessages({
    createdByUnknown: {
        id: "studio.styles-list.created-by.unknown",
        defaultMessage: "Unknown",
    },
});

let currentOnSortChange: ((colKey: string) => void) | null = null;

const makeHeader = (label: React.ReactNode, columnKey: string) => {
  let indicator = "";
  const sortSettings: SortSettings = localRepository.getOptions<SortSettings>(COLUMN_SORT_SETTINGS_STYLES);
  if (sortSettings?.column === columnKey) {
    indicator = sortSettings.direction.startsWith("asc") ? " ▲" : " ▼";
  }

  return (
      <button
          type="button"
          onClick={() => currentOnSortChange && currentOnSortChange(columnKey)}
          style={{background: "none", border: "none", padding: 0, margin: 0, cursor: "pointer"}}
          aria-label={`Sort by ${String(columnKey)}`}
      >
        <div className={"columnLabel"} style={{display: "inline-flex"}}>
          {label}
          <div className={"sortIndicator"} id={`sortIndicator_${columnKey}`}>{indicator}</div>
        </div>
      </button>
  );
}

const columns: ColumnsDescription<Style> = {
    title: {
      header: makeHeader(<FormattedMessage id="studio.styles-list.title" defaultMessage="Title"/>, "title"),
        cellContent: (style) => <Link to={`/styles/${style.id}`}>{style.title}</Link>,
        cellTooltip: (style) => style.title,
        columnWidth: ColumnWidth.flex(1),
    },
    path: {
      header: makeHeader(<FormattedMessage id="studio.styles-list.path" defaultMessage="Path"/>, "path"),
        cellContent: (style) => style.filePath,
        cellTooltip: (style) => style.filePath,
        columnWidth: ColumnWidth.flex(3),
    },
    type: {
      header: makeHeader(<FormattedMessage id="studio.styles-list.type" defaultMessage="Type"/>, "type"),
        cellContent: (style) => style.type,
        cellTooltip: (style) => style.type,
        columnWidth: ColumnWidth.TYPE_COLUMN_WIDTH,
    },
    createdBy: {
      header: makeHeader(<FormattedMessage id="studio.styles-list.created-by" defaultMessage="Created By"/>,
          "createdBy"),
        cellContent: (style) => style.createdBy ? style.createdBy.username :
            <FormattedMessage id="studio.styles-list.created-by.unknown" defaultMessage="Unknown"/>,
        cellTooltip: (style, intl) => style.createdBy ? style.createdBy.username : intl.formatMessage(MESSAGES.createdByUnknown),
        columnWidth: ColumnWidth.USERNAME_COLUMN_WIDTH,
    },
    creationTime: {
      header: makeHeader(<FormattedMessage id="studio.styles-list.date-created" defaultMessage="Date Created"/>,
          "creationTime"),
        cellContent: (style) => DateUtil.formatDateTime(style.creationTime),
        cellTooltip: (style) => DateUtil.formatDateTime(style.creationTime),
        columnWidth: ColumnWidth.DATETIME_COLUMN_WIDTH,
    },
};

export const StylesListComponent = createBasicTableComponent(columns);

function valueForColumn(item: Style, columnKey?: string | null) {
  switch (columnKey) {
  case "title":
    return (item.title || "").toString().toLowerCase();
  case "path":
    return (item.filePath || "").toString().toLowerCase();
  case "type":
    return (item.type || "").toString().toLowerCase();
  case "createdBy":
    return (item.createdBy || "").toString().toLowerCase();
  case "creationTime":
    const t = (item as any).creationTime;
    const num = typeof t === "number" ? t : Date.parse(t);
    return Number.isNaN(num) ? 0 : num;
  default:
    return "";
  }
}

const ConnectedStylesList = connect(
    (state: any, ownProps: any) => {
      const pagedState = selectors.getPagedState(state) || ({} as any);
      const rawItems: Style[] = pagedDataSelectors.getAllLoadedData<Style>(pagedState) || [];
      const sorted = sortItems(rawItems, valueForColumn, ownProps?.sortSettings);
      return {
        isPaged: true,
        canMultiSelect: config.canMultiSelect,
        noItemsFoundMessage: config.noItemsFoundMessage,
        data: sorted,
        pageSize: pagedDataSelectors.getPageSize(pagedState),
        pages: pagedDataSelectors.getAllPages<Style>(pagedState),
        isAllDataLoaded: pagedDataSelectors.isAllPagesLoaded(pagedState),
      };
    },
    (dispatch: any) => ({
      loadPage: (pageNumber: number) => dispatch(actions.requestPage(pageNumber)),
    })
)(injectIntl(StylesListComponent));

export const StylesList = (props: any) => {
  currentOnSortChange = props.onSortChange || null;
  return <ConnectedStylesList {...props} />;
};